import React, { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import {
  FaSync,
  FaDownload,
  FaUserMd,
  FaUser,
  FaSearch,
  FaDatabase,
  FaArrowLeft,
  FaTrash,
} from "react-icons/fa";
import { buildApiUrl, API_ENDPOINTS } from "../../utils/apiConfig";
import PatientProfile from "../../components/PatientProfile";
import AddPatientForm from "../../components/AddPatientForm";
import "./Admin.css";

const PatientsDatabase = () => {
  const navigate = useNavigate();
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [showPatientProfile, setShowPatientProfile] = useState(false);
  const [patientSearchTerm, setPatientSearchTerm] = useState("");
  const [showAddPatientForm, setShowAddPatientForm] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [patientToDelete, setPatientToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const fetchPatients = useCallback(async () => {
    // Prevent multiple simultaneous calls
    if (loading) return;

    setLoading(true);
    try {
      const url = patientSearchTerm
        ? `${buildApiUrl(
            API_ENDPOINTS.ADMIN.PATIENTS
          )}?search=${encodeURIComponent(patientSearchTerm)}`
        : buildApiUrl(API_ENDPOINTS.ADMIN.PATIENTS);

      console.log("🔍 Fetching patients from:", url);
      const response = await fetch(url);
      const data = await response.json();

      if (data.success) {
        console.log(
          "✅ Patients fetched successfully:",
          data.patients?.length || 0,
          "patients"
        );
        setPatients(data.patients || []);
      } else {
        console.error("Failed to fetch patients:", data.message);
      }
    } catch (error) {
      console.error("Error fetching patients:", error);
    } finally {
      setLoading(false);
    }
  }, [patientSearchTerm, loading]);

  useEffect(() => {
    fetchPatients();
    // Set up auto-refresh every 10 minutes (reduced frequency to prevent flickering)
    const interval = setInterval(fetchPatients, 600000);
    return () => clearInterval(interval);
  }, [fetchPatients]);

  // Handle ESC key to close modal
  useEffect(() => {
    const handleEscKey = (event) => {
      if (event.key === "Escape" && showDeleteConfirm) {
        cancelDeletePatient();
      }
    };

    if (showDeleteConfirm) {
      document.addEventListener("keydown", handleEscKey);
      // Prevent body scroll when modal is open
      document.body.style.overflow = "hidden";
    }

    return () => {
      document.removeEventListener("keydown", handleEscKey);
      document.body.style.overflow = "unset";
    };
  }, [showDeleteConfirm]);

  // Auto-fetch patients when search term changes (with debounce)
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (patientSearchTerm !== undefined) {
        fetchPatients();
      }
    }, 1000); // 1 second debounce (increased to reduce API calls)

    return () => clearTimeout(timeoutId);
  }, [patientSearchTerm, fetchPatients]);

  const refreshData = async () => {
    setRefreshing(true);
    await fetchPatients();
    setRefreshing(false);
  };

  const handlePatientInfo = async (patientEmail, patientPhone) => {
    try {
      const identifier = patientEmail || patientPhone;
      if (!identifier) {
        console.error("No identifier available for patient");
        setSelectedPatient(null);
        return;
      }

      // Clear previous patient data immediately to prevent showing stale data
      setSelectedPatient(null);

      const response = await fetch(
        buildApiUrl(API_ENDPOINTS.ADMIN.PATIENT_INFO) + "/" + identifier
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data.success && data.patient) {
        setSelectedPatient(data.patient);
        setShowPatientProfile(true);
      } else {
        console.error("No patient data in response:", data);
        setSelectedPatient(null);
      }
    } catch (error) {
      console.error("Error fetching patient info:", error);
      setSelectedPatient(null);
    }
  };

  const closePatientProfile = () => {
    setShowPatientProfile(false);
    setSelectedPatient(null);
  };

  const handlePatientProfileRefresh = async () => {
    // Refresh patients data to ensure notes are up to date
    fetchPatients();

    // If a patient profile is currently open, refresh that patient's data
    if (selectedPatient && showPatientProfile) {
      const identifier = selectedPatient.email || selectedPatient.phone;
      if (identifier) {
        try {
          console.log("🔄 Refreshing current patient data...");
          const response = await fetch(
            buildApiUrl(API_ENDPOINTS.ADMIN.PATIENT_INFO) + "/" + identifier
          );

          if (response.ok) {
            const data = await response.json();
            if (data.success && data.patient) {
              console.log("✅ Patient data refreshed:", data.patient);
              setSelectedPatient(data.patient);
            }
          }
        } catch (error) {
          console.error("❌ Error refreshing patient data:", error);
        }
      }
    }
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setPatientSearchTerm(value);
  };

  const handleClearSearch = () => {
    setPatientSearchTerm("");
  };

  const downloadCSV = async () => {
    try {
      const response = await fetch(buildApiUrl(`/api/patients/files`));
      const data = await response.json();

      if (data.success) {
        alert(`CSV file location: ${data.filePaths.patients}`);
      }
    } catch (error) {
      console.error("Error downloading file:", error);
    }
  };

  const handleDeletePatient = (patient) => {
    setPatientToDelete(patient);
    setShowDeleteConfirm(true);
  };

  const confirmDeletePatient = async () => {
    if (!patientToDelete) return;

    setDeleting(true);
    try {
      const response = await fetch(
        buildApiUrl(API_ENDPOINTS.ADMIN.DELETE_PATIENT) +
          `/${patientToDelete.patient_id || patientToDelete.PatientID}`,
        {
          method: "DELETE",
        }
      );

      const data = await response.json();

      if (data.success) {
        // Remove patient from local state
        setPatients(
          patients.filter(
            (p) =>
              (p.patient_id || p.PatientID) !==
              (patientToDelete.patient_id || patientToDelete.PatientID)
          )
        );
        alert("Patient deleted successfully!");
      } else {
        alert(`Error deleting patient: ${data.message}`);
      }
    } catch (error) {
      console.error("Error deleting patient:", error);
      alert("Error deleting patient. Please try again.");
    } finally {
      setDeleting(false);
      setShowDeleteConfirm(false);
      setPatientToDelete(null);
    }
  };

  const cancelDeletePatient = () => {
    setShowDeleteConfirm(false);
    setPatientToDelete(null);
  };

  if (loading) {
    return (
      <div className="admin-loading">
        <div className="loading-spinner"></div>
        <p>Loading patients database...</p>
      </div>
    );
  }

  return (
    <motion.div
      className="admin-page"
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
    >
      <div className="admin-header">
        <div className="header-content">
          <div className="header-left">
            <button
              className="back-btn"
              onClick={() => navigate("/admin")}
              title="Back to Dashboard"
            >
              <FaArrowLeft /> Back to Dashboard
            </button>
            <h1>👥 Patients Database</h1>
          </div>
          <div className="header-right">
            <button
              className="add-patient-btn"
              onClick={() => setShowAddPatientForm(true)}
              title="Add New Patient"
            >
              <FaUser /> Add New Patient
            </button>
            <button
              className="refresh-btn"
              onClick={refreshData}
              disabled={refreshing}
            >
              <FaSync className={refreshing ? "spinning" : ""} />
              {refreshing ? " Refreshing..." : " Refresh"}
            </button>
            <button className="download-btn" onClick={downloadCSV}>
              <FaDownload /> Download CSV
            </button>
          </div>
        </div>
      </div>

      <div className="data-section">
        <div className="section-header">
          <h2>
            <FaDatabase /> Patients Database
            <span className="patient-count">({patients.length} total)</span>
          </h2>
        </div>

        {/* Patient Search */}
        <div className="search-section">
          <div className="search-container">
            <FaUser className="search-icon" />
            <input
              type="text"
              placeholder="Search patients by name, email, or phone..."
              value={patientSearchTerm}
              onChange={handleSearchChange}
              onKeyPress={(e) => {
                if (e.key === "Enter") {
                  fetchPatients();
                }
              }}
              className="patient-search-input"
            />
            {patientSearchTerm && (
              <button
                className="clear-search-btn"
                onClick={handleClearSearch}
                title="Clear search"
              >
                ×
              </button>
            )}
          </div>
          <button
            className="search-btn"
            onClick={fetchPatients}
            disabled={loading}
          >
            <FaSearch /> {loading ? "Searching..." : "Search"}
          </button>
          {patientSearchTerm && (
            <div className="search-info">
              {loading ? (
                <span>🔍 Searching...</span>
              ) : (
                <span>
                  🔍 Found {patients.length} patient
                  {patients.length !== 1 ? "s" : ""}
                  {patientSearchTerm && ` for "${patientSearchTerm}"`}
                </span>
              )}
            </div>
          )}
        </div>

        {/* Patients Table */}
        {loading ? (
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p>Loading patients data...</p>
          </div>
        ) : patients.length === 0 ? (
          <div className="no-data">
            <p>No patients found in the database.</p>
          </div>
        ) : (
          <div className="data-table">
            <table>
              <thead>
                <tr>
                  <th>Patient ID</th>
                  <th>Name</th>
                  <th>Contact Info</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {patients.map((patient, index) => (
                  <tr key={index}>
                    <td className="patient-id">
                      <code>{patient.PatientID || patient.patient_id}</code>
                    </td>
                    <td className="patient-name">
                      <strong>{patient.Name || patient.name || "N/A"}</strong>
                    </td>
                    <td className="contact-info">
                      <div>📧 {patient.Email || patient.email || "N/A"}</div>
                      <div>📞 {patient.Phone || patient.phone || "N/A"}</div>
                    </td>
                    <td>
                      <div className="action-buttons">
                        <button
                          className="patient-info-btn"
                          onClick={() => {
                            handlePatientInfo(
                              patient.Email || patient.email,
                              patient.Phone || patient.phone
                            );
                          }}
                          title="View Patient Profile"
                          disabled={
                            !(patient.Email || patient.email) &&
                            !(patient.Phone || patient.phone)
                          }
                        >
                          <FaUserMd /> Profile
                        </button>
                        <button
                          className="delete-patient-btn"
                          onClick={() => handleDeletePatient(patient)}
                          title="Delete Patient"
                        >
                          <FaTrash />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Patient Profile Modal */}
      {showPatientProfile && (
        <PatientProfile
          patient={selectedPatient}
          onClose={closePatientProfile}
          onRefresh={handlePatientProfileRefresh}
        />
      )}

      {/* Add New Patient Form Modal */}
      {showAddPatientForm && (
        <AddPatientForm
          onClose={() => setShowAddPatientForm(false)}
          onPatientAdded={() => {
            setShowAddPatientForm(false);
            fetchPatients();
          }}
        />
      )}

      {/* Delete Confirmation Dialog */}
      {showDeleteConfirm && (
        <div
          className="modal-overlay"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              cancelDeletePatient();
            }
          }}
        >
          <div className="modal-content delete-confirm-modal">
            <button
              className="modal-close-btn"
              onClick={cancelDeletePatient}
              disabled={deleting}
              title="Close"
            >
              ×
            </button>
            <h3>⚠️ Delete Patient</h3>
            <p>
              Are you sure you want to delete{" "}
              <strong>{patientToDelete?.Name}</strong>? This action will
              permanently remove:
            </p>
            <ul>
              <li>Patient information</li>
              <li>All appointments</li>
              <li>All notes</li>
              <li>All reports and uploaded files</li>
              <li>All contact history</li>
            </ul>
            <p className="warning-text">
              <strong>This action cannot be undone!</strong>
            </p>
            <div className="modal-actions">
              <button
                className="cancel-btn"
                onClick={cancelDeletePatient}
                disabled={deleting}
              >
                Cancel
              </button>
              <button
                className="delete-btn"
                onClick={confirmDeletePatient}
                disabled={deleting}
              >
                {deleting ? "Deleting..." : "Delete Patient"}
              </button>
            </div>
          </div>
        </div>
      )}
    </motion.div>
  );
};

export default PatientsDatabase;
